import { defineStore } from 'pinia'
import { Preferences } from '@capacitor/preferences'
import NotificationService, { Notification, NotificationFilter } from '@/services/NotificationService'
import { SchultenMedia } from '@/services/DataService'
import { useNetworkStatus } from '@/composables/useNetworkStatus'

const FILTER_KEY = 'notification_filter'
const FILTERS: NotificationFilter[] = ['all', 'unread']

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  currentFilter: NotificationFilter;
  totalNotifications: number;
  currentPage: number;
  lastPage: number;
  lastFetchTime: number;
  isDrawerOpen: boolean;
}

export const useNotificationStore = defineStore('notifications', {
  state: (): NotificationState => ({
    notifications: [],
    unreadCount: 0,
    isLoading: false,
    error: null,
    currentFilter: 'unread',
    totalNotifications: 0,
    currentPage: 1,
    lastPage: 1,
    lastFetchTime: 0,
    isDrawerOpen: false
  }),

  actions: {
    async fetchNotifications(filter?: NotificationFilter, force: boolean = false) {
      // Don't fetch notifications if user is not authenticated
      if (!SchultenMedia.Authenticated) {
        return;
      }

      // Offline: skip the call entirely (it would just fail). The offline
      // banner already tells the user there's no connection — not an error.
      const { isOnline } = useNetworkStatus();
      if (!isOnline.value) {
        return;
      }

      // Debounce notifications fetch - only fetch if:
      // 1. It's a forced refresh, or
      // 2. It's been more than 30 seconds since last fetch
      const now = Date.now();
      if (!force && (now - this.lastFetchTime) < 30000) {
        return;
      }

      if (filter) {
        this.setFilter(filter);
      }

      if (this.isLoading) {
        return;
      }

      this.isLoading = true;
      this.error = null;
      try {
        const response = await NotificationService.getAllNotifications('all'); // Always fetch all to get accurate unread count

        if (!response.success) {
          this.error = response.message;
          return;
        }

        // Store all notifications for unread count
        const allNotifications = response.data.data;
        this.unreadCount = allNotifications.filter((n: Notification) => !n.read_at).length;

        // If we're fetching with a specific filter, get those notifications
        if (this.currentFilter !== 'all') {
          const filteredResponse = await NotificationService.getAllNotifications(this.currentFilter);
          if (filteredResponse.success) {
            this.notifications = filteredResponse.data.data;
            this.totalNotifications = filteredResponse.data.meta.total;
            this.currentPage = filteredResponse.data.meta.current_page;
            this.lastPage = filteredResponse.data.meta.last_page;
          }
        } else {
          this.notifications = allNotifications;
          this.totalNotifications = response.data.meta.total;
          this.currentPage = response.data.meta.current_page;
          this.lastPage = response.data.meta.last_page;
        }

        this.lastFetchTime = now;
      } catch (error) {
        this.error = 'Failed to fetch notifications';
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    async markAsRead(notificationId: string, callback?: any) {
      // Don't mark as read if user is not authenticated
      if (!SchultenMedia.Authenticated) {
        return;
      }

      try {
        const response = await NotificationService.markAsRead(notificationId);

        if (!response.success) {
          console.error('Error marking notification as read:', response.message);
          return;
        }

        // Update the notification in our list
        const notification = this.notifications.find(n => n.id === notificationId);
        if (notification) {
          notification.read_at = new Date().toISOString();

          if (callback) {
            callback(notification);
          }
        }

        // Force refresh to get accurate unread count
        await this.fetchNotifications(undefined, true);
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    },

    async markAllAsRead() {
      // Don't mark all as read if user is not authenticated
      if (!SchultenMedia.Authenticated) {
        return;
      }

      try {
        const response = await NotificationService.markAllAsRead(this.notifications);

        if (!response.success) {
          console.error('Error marking all notifications as read:', response.message);
          return;
        }

        this.notifications.forEach(notification => {
          if (!notification.read_at) {
            notification.read_at = new Date().toISOString();
          }
        });

        // Force refresh to get accurate unread count
        await this.fetchNotifications(undefined, true);
      } catch (error) {
        console.error('Error marking all notifications as read:', error);
      }
    },

    /**
     * The drawer used to reset to 'unread' on every open. Persist the choice
     * instead, so reopening the drawer shows the list the user last picked.
     * Fire-and-forget: the filter is already applied in memory, a failed write
     * only costs the preference on the next app start.
     */
    setFilter(filter: NotificationFilter) {
      this.currentFilter = filter;
      Preferences.set({ key: FILTER_KEY, value: filter }).catch(() => {});
    },

    async loadFilterPreference() {
      try {
        const { value } = await Preferences.get({ key: FILTER_KEY });
        if (FILTERS.includes(value as NotificationFilter)) {
          this.currentFilter = value as NotificationFilter;
        }
      } catch {
        /* keep the default */
      }
    },

    updateUnreadCount() {
      this.unreadCount = this.notifications.filter(n => !n.read_at).length;
    },

    openDrawer() {
      this.isDrawerOpen = true;
    },

    closeDrawer() {
      this.isDrawerOpen = false;
    }
  }
}); 